import { useState, useEffect } from 'react';
import ReportDownloadModal from '../../../components/ReportDownloadModal/ReportDownloadModal';

const OutputsAndLiveDemo = () => {
  const [activeTab, setActiveTab] = useState<'balances' | 'table' | 'historical' | 'ripcord' | 'faq'>('balances');
  const [reserveData, setReserveData] = useState({
    cash: 0,
    treasuryBonds: 0,
    preciousMetals: 0,
    goldReserves: 0,
    ethereum: 8_478_596,
    solana: 20_843_503,
    lastUpdate: new Date().toISOString(),
  });

  // Simulate daily data fetch
  useEffect(() => {
    const interval = setInterval(() => {
      const variance = 0.001; // 0.1% variance
      setReserveData(prev => ({
        cash: 0,
        treasuryBonds: 0,
        preciousMetals: 0,
        goldReserves: 0,
        ethereum: prev.ethereum * (1 + (Math.random() - 0.5) * variance),
        solana: prev.solana * (1 + (Math.random() - 0.5) * variance),
        lastUpdate: new Date().toISOString(),
      }));
    }, 30000); // Update every 30 seconds to simulate real-time

    return () => clearInterval(interval);
  }, []);

  const totalCryptoAssets = reserveData.ethereum + reserveData.solana;
  const totalReserves = reserveData.cash + reserveData.treasuryBonds + reserveData.preciousMetals + reserveData.goldReserves + totalCryptoAssets;
  const cashPercentage = (reserveData.cash / totalReserves) * 100;
  const bondsPercentage = (reserveData.treasuryBonds / totalReserves) * 100;
  const preciousMetalsPercentage = (reserveData.preciousMetals / totalReserves) * 100;
  const goldReservesPercentage = (reserveData.goldReserves / totalReserves) * 100;
  const cryptoAssetsPercentage = (totalCryptoAssets / totalReserves) * 100;
  const ethereumPercentage = (reserveData.ethereum / totalCryptoAssets) * 100;
  const solanaPercentage = (reserveData.solana / totalCryptoAssets) * 100;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDownloadReport = () => {
    setIsModalOpen(true);
  };

  return (
    <section className="section-padding bg-gray-50">
      <div className="container-custom">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-12 sm:mb-16 text-accent">
          Real-Time Proof of Reserves Outputs & Live Demo
        </h2>

        {/* Add visual representation */}
        <div className="mb-12 max-w-4xl mx-auto">
          <img src="/2.png" alt="Real-Time Dashboard" className="w-full h-auto rounded-2xl shadow-2xl" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Left Column */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
              <h3 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900">
                1. CPA Attestation Reports under AICPA Standards
              </h3>
              <p className="text-gray-700 mb-6">
                The Audit Financier Team's Proof of Reserves attestation reporting is issued under standards promulgated by the American
                Institute of Certified Public Accountants (AICPA).
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    The Audit Financier Team customers can make the Independent Accountant's Report available to customers directly (either
                    permissioned or publicly) for on-demand download.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    Under AICPA Standards The Audit Financier Team can provide Agreed Upon Procedures where we provide Procedures and
                    findings over the subject matter, or Examinations where we provide an opinion over management's
                    assertion being fairly stated or the subject matter being in accordance with (or based on) the
                    criteria, in all material respects.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    The same reports used by top issuers are available to users in real-time!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
              <h3 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900">
                2. Embeddable Widget
              </h3>
              <p className="text-gray-700 mb-6">
                The Audit Financier Team's widget enables customers to provide Real-Time Proof of Reserves attestations to interested parties.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    The widget hosts a dashboard showing on-chain liabilities and off-chain assets, from multiple
                    blockchains, custodians, and financial institutions.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    The widget provides the most recent downloadable attest report as well as historical attestation
                    reporting.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">
                    The Audit Financier Team's service is flexible to any blockchain source and any custodian or financial institution API.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Live Demo */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-soft">
            <h3 className="text-lg sm:text-xl font-bold mb-4 text-center">
              View our last audits and reports
            </h3>

            <div className="space-y-4 mb-6 px-2 sm:px-0">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700 text-sm">
                  Choose from different reports to view below. Documents are password protected and view-only.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700 text-sm">
                  View the different tokens and products balances.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent flex items-center justify-center mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700 text-sm">
                  View the FAQ
                </p>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-2 border-b border-gray-200 mb-6 overflow-x-auto pb-2">
              <button
                onClick={() => setActiveTab('balances')}
                className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'balances'
                    ? 'text-accent border-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Balances
              </button>
              <button
                onClick={() => setActiveTab('table')}
                className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'table'
                    ? 'text-accent border-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setActiveTab('historical')}
                className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'historical'
                    ? 'text-accent border-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Historical
              </button>
              <button
                onClick={() => setActiveTab('ripcord')}
                className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'ripcord'
                    ? 'text-accent border-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Ripcord
              </button>
              <button
                onClick={() => setActiveTab('faq')}
                className={`px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === 'faq'
                    ? 'text-accent border-accent'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                FAQ
              </button>
            </div>

            {/* Dashboard Content */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
              <p className="text-sm text-gray-600 mb-6 text-center">
                Welcome to USD CoinVertible, real-time reserve balance dashboard.
              </p>

              {/* Balances Tab */}
              {activeTab === 'balances' && (
                <>
                  <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm mb-6">
                    <h4 className="text-base sm:text-lg font-semibold mb-6">Asset Distribution</h4>

                    {/* Bar Chart */}
                    <div className="relative h-48 sm:h-64 flex items-end justify-center gap-2 sm:gap-4 mb-4">
                      <div className="text-xs text-gray-500 absolute left-0 top-0 flex flex-col justify-between h-full py-2">
                        <span>30M</span>
                        <span>15M</span>
                        <span>12M</span>
                        <span>9M</span>
                        <span>6M</span>
                        <span>3M</span>
                        <span>0M</span>
                      </div>
                      <div className="flex-1 ml-12 sm:ml-16 h-full flex items-end justify-center gap-2 sm:gap-4">
                        <div className="flex flex-col items-center flex-1 max-w-[60px] sm:max-w-[80px]">
                          <div
                            className="w-full bg-green-400 rounded-t transition-all duration-500 relative group hover:bg-green-500"
                            style={{ height: `${(reserveData.cash / 18_000_000) * 100}%` }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {formatCurrency(reserveData.cash)}
                            </div>
                          </div>
                          <span className="text-xs text-gray-700 mt-2 font-medium text-center">Cash</span>
                          <div className="flex items-center gap-1 mt-1">
                            {/* Enhanced Mini Candlestick - Full Height */}
                            <div className="relative w-4 flex items-end justify-center" style={{ height: '32px' }}>
                              <div className="absolute w-0.5 bg-gradient-to-t from-green-600 to-green-300" style={{ height: '100%' }}></div>
                              <div className="absolute w-3 bg-green-400 border border-green-600 rounded-sm shadow-sm" style={{ height: '40%', bottom: '30%' }}></div>
                            </div>
                            <span className="text-xs text-gray-500">{cashPercentage.toFixed(2)}%</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center flex-1 max-w-[60px] sm:max-w-[80px]">
                          <div
                            className="w-full bg-gray-800 rounded-t transition-all duration-500 relative group hover:bg-gray-700"
                            style={{ height: `${(reserveData.treasuryBonds / 18_000_000) * 100}%` }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {formatCurrency(reserveData.treasuryBonds)}
                            </div>
                          </div>
                          <span className="text-xs text-gray-700 mt-2 font-medium text-center">Treasury Notes</span>
                          <div className="flex items-center gap-1 mt-1">
                            {/* Enhanced Mini Candlestick - Full Height */}
                            <div className="relative w-4 flex items-end justify-center" style={{ height: '32px' }}>
                              <div className="absolute w-0.5 bg-gradient-to-t from-gray-800 to-gray-400" style={{ height: '100%' }}></div>
                              <div className="absolute w-3 bg-gray-600 border border-gray-800 rounded-sm shadow-sm" style={{ height: '40%', bottom: '30%' }}></div>
                            </div>
                            <span className="text-xs text-gray-500">{bondsPercentage.toFixed(2)}%</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center flex-1 max-w-[60px] sm:max-w-[80px]">
                          <div
                            className="w-full bg-yellow-400 rounded-t transition-all duration-500 relative group hover:bg-yellow-500"
                            style={{ height: `${(reserveData.preciousMetals / 18_000_000) * 100}%` }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {formatCurrency(reserveData.preciousMetals)}
                            </div>
                          </div>
                          <span className="text-xs text-gray-700 mt-2 font-medium text-center">Precious Metals</span>
                          <div className="flex items-center gap-1 mt-1">
                            {/* Enhanced Mini Candlestick - Full Height */}
                            <div className="relative w-4 flex items-end justify-center" style={{ height: '32px' }}>
                              <div className="absolute w-0.5 bg-gradient-to-t from-yellow-700 to-yellow-300" style={{ height: '100%' }}></div>
                              <div className="absolute w-3 bg-yellow-400 border border-yellow-600 rounded-sm shadow-sm" style={{ height: '40%', bottom: '30%' }}></div>
                            </div>
                            <span className="text-xs text-gray-500">{preciousMetalsPercentage.toFixed(2)}%</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center flex-1 max-w-[60px] sm:max-w-[80px]">
                          <div
                            className="w-full bg-amber-500 rounded-t transition-all duration-500 relative group hover:bg-amber-600"
                            style={{ height: `${(reserveData.goldReserves / 18_000_000) * 100}%` }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {formatCurrency(reserveData.goldReserves)}
                            </div>
                          </div>
                          <span className="text-xs text-gray-700 mt-2 font-medium text-center">Gold Reserves</span>
                          <div className="flex items-center gap-1 mt-1">
                            {/* Enhanced Mini Candlestick - Full Height */}
                            <div className="relative w-4 flex items-end justify-center" style={{ height: '32px' }}>
                              <div className="absolute w-0.5 bg-gradient-to-t from-amber-800 to-amber-400" style={{ height: '100%' }}></div>
                              <div className="absolute w-3 bg-amber-500 border border-amber-700 rounded-sm shadow-sm" style={{ height: '40%', bottom: '30%' }}></div>
                            </div>
                            <span className="text-xs text-gray-500">{goldReservesPercentage.toFixed(2)}%</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-center flex-1 max-w-[60px] sm:max-w-[80px]">
                          <div
                            className="w-full bg-blue-500 rounded-t transition-all duration-500 relative group hover:bg-blue-600"
                            style={{ height: `${(totalCryptoAssets / 30_000_000) * 100}%` }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              {formatCurrency(totalCryptoAssets)}
                            </div>
                          </div>
                          <span className="text-xs text-gray-700 mt-2 font-medium text-center">Crypto Assets</span>
                          <div className="flex items-center gap-1 mt-1">
                            {/* Enhanced Mini Candlestick - Full Height */}
                            <div className="relative w-4 flex items-end justify-center" style={{ height: '32px' }}>
                              <div className="absolute w-1 bg-gradient-to-t from-blue-900 to-blue-300" style={{ height: '100%' }}></div>
                              <div className="absolute w-3 bg-blue-500 border-2 border-blue-700 rounded-sm shadow-md" style={{ height: '50%', bottom: '25%' }}></div>
                            </div>
                            <span className="text-xs font-semibold text-blue-600">{cryptoAssetsPercentage.toFixed(2)}%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded"></div>
                        <span className="text-xs text-gray-600">Cash: {formatCurrency(reserveData.cash)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-gray-800 rounded"></div>
                        <span className="text-xs text-gray-600">T-Notes: {formatCurrency(reserveData.treasuryBonds)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-400 rounded"></div>
                        <span className="text-xs text-gray-600">Precious Metals: {formatCurrency(reserveData.preciousMetals)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-xs text-gray-600">Gold: {formatCurrency(reserveData.goldReserves)}</span>
                      </div>
                      <div className="flex items-center gap-2 col-span-2">
                        <div className="w-3 h-3 bg-blue-500 rounded"></div>
                        <span className="text-xs text-gray-600">Crypto Assets Total: {formatCurrency(totalCryptoAssets)} ({cryptoAssetsPercentage.toFixed(2)}%)</span>
                      </div>
                    </div>
                  </div>

                  {/* Candlestick Chart View */}
                  <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm mb-6">
                    <h4 className="text-base sm:text-lg font-semibold mb-6">Percentage Distribution (Candlestick View)</h4>
                    
                    <div className="relative h-64 flex items-end justify-center gap-8 mb-4">
                      {/* Y-axis labels */}
                      <div className="text-xs text-gray-500 absolute left-0 top-0 flex flex-col justify-between h-full py-2">
                        <span>100%</span>
                        <span>80%</span>
                        <span>60%</span>
                        <span>40%</span>
                        <span>20%</span>
                        <span>0%</span>
                      </div>
                      
                      {/* Grid lines */}
                      <div className="absolute inset-0 ml-12 flex flex-col justify-between">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className="border-t border-gray-200" />
                        ))}
                      </div>

                      {/* Candlesticks */}
                      <div className="flex-1 ml-12 h-full flex items-end justify-center gap-8 relative z-10">
                        {/* Cash Candlestick */}
                        <div className="flex flex-col items-center flex-1 max-w-[80px]">
                          <div className="relative w-full flex justify-center" style={{ height: '100%' }}>
                            {/* Full Height Wick */}
                            <div
                              className="absolute w-1.5 bg-gradient-to-t from-green-700 via-green-500 to-green-300"
                              style={{
                                bottom: '0%',
                                height: `${Math.max(cashPercentage, 2)}%`
                              }}
                            />
                            {/* Body */}
                            <div
                              className="absolute w-10 bg-green-400 border-2 border-green-600 shadow-lg rounded-sm"
                              style={{
                                bottom: `${Math.max(cashPercentage - 8, 0)}%`,
                                height: '16%',
                                minHeight: '14px'
                              }}
                            />
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-xs font-semibold text-gray-900">{cashPercentage.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">Cash</div>
                          </div>
                        </div>

                        {/* Treasury Notes Candlestick */}
                        <div className="flex flex-col items-center flex-1 max-w-[80px]">
                          <div className="relative w-full flex justify-center" style={{ height: '100%' }}>
                            {/* Full Height Wick */}
                            <div
                              className="absolute w-1.5 bg-gradient-to-t from-gray-800 via-gray-600 to-gray-400"
                              style={{
                                bottom: '0%',
                                height: `${Math.max(bondsPercentage, 2)}%`
                              }}
                            />
                            {/* Body */}
                            <div
                              className="absolute w-10 bg-gray-600 border-2 border-gray-800 shadow-lg rounded-sm"
                              style={{
                                bottom: `${Math.max(bondsPercentage - 8, 0)}%`,
                                height: '16%',
                                minHeight: '14px'
                              }}
                            />
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-xs font-semibold text-gray-900">{bondsPercentage.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">Treasury Notes</div>
                          </div>
                        </div>

                        {/* Precious Metals Candlestick */}
                        <div className="flex flex-col items-center flex-1 max-w-[80px]">
                          <div className="relative w-full flex justify-center" style={{ height: '100%' }}>
                            {/* Full Height Wick */}
                            <div
                              className="absolute w-1.5 bg-gradient-to-t from-yellow-700 via-yellow-500 to-yellow-300"
                              style={{
                                bottom: '0%',
                                height: `${Math.max(preciousMetalsPercentage, 2)}%`
                              }}
                            />
                            {/* Body */}
                            <div
                              className="absolute w-10 bg-yellow-400 border-2 border-yellow-600 shadow-lg rounded-sm"
                              style={{
                                bottom: `${Math.max(preciousMetalsPercentage - 8, 0)}%`,
                                height: '16%',
                                minHeight: '14px'
                              }}
                            />
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-xs font-semibold text-gray-900">{preciousMetalsPercentage.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">Precious Metals</div>
                          </div>
                        </div>

                        {/* Gold Reserves Candlestick */}
                        <div className="flex flex-col items-center flex-1 max-w-[80px]">
                          <div className="relative w-full flex justify-center" style={{ height: '100%' }}>
                            {/* Full Height Wick */}
                            <div
                              className="absolute w-1.5 bg-gradient-to-t from-amber-800 via-amber-600 to-amber-400"
                              style={{
                                bottom: '0%',
                                height: `${Math.max(goldReservesPercentage, 2)}%`
                              }}
                            />
                            {/* Body */}
                            <div
                              className="absolute w-10 bg-amber-500 border-2 border-amber-700 shadow-lg rounded-sm"
                              style={{
                                bottom: `${Math.max(goldReservesPercentage - 8, 0)}%`,
                                height: '16%',
                                minHeight: '14px'
                              }}
                            />
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-xs font-semibold text-gray-900">{goldReservesPercentage.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">Gold Reserves</div>
                          </div>
                        </div>

                        {/* Crypto Assets Candlestick */}
                        <div className="flex flex-col items-center flex-1 max-w-[80px]">
                          <div className="relative w-full flex justify-center" style={{ height: '100%' }}>
                            {/* Full Height Wick - using entire Y axis */}
                            <div
                              className="absolute w-2 bg-gradient-to-t from-blue-900 via-blue-600 to-blue-300"
                              style={{
                                bottom: '0%',
                                height: `${cryptoAssetsPercentage}%`
                              }}
                            />
                            {/* Body at the top */}
                            <div
                              className="absolute w-12 bg-blue-500 border-2 border-blue-800 shadow-lg rounded-sm"
                              style={{
                                bottom: `${cryptoAssetsPercentage - 12}%`,
                                height: '20%',
                                minHeight: '16px'
                              }}
                            />
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-xs font-semibold text-gray-900">{cryptoAssetsPercentage.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">Crypto Assets</div>
                            <div className="text-xs text-blue-600 font-medium">{formatCurrency(totalCryptoAssets)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Crypto Assets Insider Chart */}
                  <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm mb-6">
                    <h4 className="text-base sm:text-lg font-semibold mb-4">Crypto Assets Breakdown</h4>
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-700">Total Crypto Assets</span>
                        <span className="text-lg font-bold text-blue-600">{formatCurrency(totalCryptoAssets)}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Ethereum */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z"/>
                            </svg>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">Ethereum</div>
                            <div className="text-xs text-gray-500">ETH</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Balance</span>
                            <span className="text-sm font-semibold text-gray-900">{formatCurrency(reserveData.ethereum)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Share</span>
                            <span className="text-sm font-semibold text-purple-600">{ethereumPercentage.toFixed(2)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${ethereumPercentage}%` }}></div>
                          </div>
                        </div>
                      </div>

                      {/* Solana */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg overflow-hidden bg-white">
                            <img
                              src="/solana-logo.png"
                              alt="Solana"
                              className="w-7 h-7 object-contain"
                            />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">Solana</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full" style={{background: 'linear-gradient(135deg, #9945FF, #14F195)'}}></span>
                              SOL
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Balance</span>
                            <span className="text-sm font-semibold text-gray-900">{formatCurrency(reserveData.solana)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Share</span>
                            <span className="text-sm font-semibold text-green-600">{solanaPercentage.toFixed(2)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${solanaPercentage}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mini stacked bar */}
                    <div className="mt-4">
                      <div className="flex h-8 rounded-lg overflow-hidden">
                        <div
                          className="bg-purple-500 flex items-center justify-center text-white text-xs font-semibold"
                          style={{ width: `${ethereumPercentage}%` }}
                        >
                          {ethereumPercentage > 15 && `ETH ${ethereumPercentage.toFixed(0)}%`}
                        </div>
                        <div
                          className="bg-green-500 flex items-center justify-center text-white text-xs font-semibold"
                          style={{ width: `${solanaPercentage}%` }}
                        >
                          {solanaPercentage > 15 && `SOL ${solanaPercentage.toFixed(0)}%`}
                        </div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-purple-500 rounded"></div>
                          Ethereum: {formatCurrency(reserveData.ethereum)}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded"></div>
                          Solana: {formatCurrency(reserveData.solana)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <div className="text-xs text-gray-500">Last Updated: {formatDate(reserveData.lastUpdate)}</div>
                    <button
                      onClick={handleDownloadReport}
                      className="mt-1 bg-black text-white px-4 py-1 rounded text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      View Reports
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-gray-600">Live</span>
                  </div>
                </div>
                </>
              )}

              {/* Table Tab */}
              {activeTab === 'table' && (
                <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Asset Type</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Percentage</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="text-sm font-medium text-gray-900">Cash Reserves</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-semibold">
                          {formatCurrency(reserveData.cash)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {cashPercentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                              <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="text-sm font-medium text-gray-900">U.S. Treasury Notes</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-semibold">
                          {formatCurrency(reserveData.treasuryBonds)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {bondsPercentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c.25.112.526.174.818.174.292 0 .569-.062.818-.174L5 10.274zm10 0l-.818 2.552c.25.112.526.174.818.174.292 0 .569-.062.818-.174L15 10.274z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="text-sm font-medium text-gray-900">Precious Metals Attestations</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-semibold">
                          {formatCurrency(reserveData.preciousMetals)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {preciousMetalsPercentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                              <svg className="w-6 h-6 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="text-sm font-medium text-gray-900">Gold Reserves</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-semibold">
                          {formatCurrency(reserveData.goldReserves)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {goldReservesPercentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </div>
                            <div className="text-sm font-medium text-gray-900">Crypto Assets</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-semibold">
                          {formatCurrency(totalCryptoAssets)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 font-semibold text-blue-600">
                          {cryptoAssetsPercentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Verified
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Historical Tab */}
              {activeTab === 'historical' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                    <h4 className="text-base sm:text-lg font-semibold mb-4">30-Day Reserve History</h4>
                    <div className="relative h-48 sm:h-64">
                      <svg className="w-full h-full" viewBox="0 0 600 200">
                        <defs>
                          <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#EF4444" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#EF4444" stopOpacity="0.05" />
                          </linearGradient>
                        </defs>
                        {/* Grid lines */}
                        {[0, 1, 2, 3, 4].map(i => (
                          <line key={i} x1="50" y1={40 + i * 40} x2="580" y2={40 + i * 40} stroke="#E5E7EB" strokeWidth="1" />
                        ))}
                        {/* Y-axis labels - showing 21% decrease from ~37M to ~29M */}
                        <text x="5" y="45" fontSize="10" fill="#6B7280">$37M</text>
                        <text x="5" y="85" fontSize="10" fill="#6B7280">$34M</text>
                        <text x="5" y="125" fontSize="10" fill="#6B7280">$32M</text>
                        <text x="5" y="165" fontSize="10" fill="#6B7280">$29M</text>
                        {/* Trend line showing 21% decrease */}
                        <path
                          d="M50,50 Q150,60 250,75 T450,110 Q500,130 580,160"
                          fill="url(#chartGradient)"
                          stroke="#EF4444"
                          strokeWidth="2"
                        />
                        {/* Start point marker */}
                        <circle cx="50" cy="50" r="4" fill="#10B981" />
                        <text x="40" y="40" fontSize="9" fill="#10B981">Start</text>
                        {/* End point marker */}
                        <circle cx="580" cy="160" r="4" fill="#EF4444" />
                        <text x="560" y="175" fontSize="9" fill="#EF4444">-21%</text>
                      </svg>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 mt-4 text-center">
                      Reserve balance decreased 21% over the past 30 days (from $37.1M to $29.3M)
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                    <h4 className="text-base sm:text-lg font-semibold mb-4">Recent Updates</h4>
                    <div className="space-y-3">
                      {[0, 1, 2, 3, 4].map(i => {
                        const date = new Date();
                        date.setDate(date.getDate() - i);
                        return (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <span className="text-xs sm:text-sm text-gray-600">{formatDate(date.toISOString())}</span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-900">Verified</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Ripcord Tab */}
              {activeTab === 'ripcord' && (
                <div className="space-y-4">
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-green-900 mb-2">All Systems Operational</h4>
                        <p className="text-sm text-green-800">
                          Reserve ratio is healthy at 100.00%. No alerts or warnings at this time.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <h4 className="text-lg font-semibold mb-4">Alert Thresholds</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Minimum Reserve Ratio</p>
                          <p className="text-xs text-gray-500">Trigger warning if reserves drop below threshold</p>
                        </div>
                        <span className="text-sm font-semibold text-green-600">100%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <h4 className="text-lg font-semibold mb-4">Monitoring Status</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Cash Reserves: Active</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Treasury Notes: Active</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">API Connection: Stable</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Data Sync: Real-time</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* FAQ Tab */}
              {activeTab === 'faq' && (
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-semibold mb-6">How We Verify Your Assets</h4>
                  
                  <p className="text-sm text-gray-600 mb-6">
                    Audit Financier is the undisputed global leader in auditing and inspection services for precious metals funds, mining companies, refineries, custodians, and brokerage firms. During each audit, the team:
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">
                          Verifies that the metals are physically segregated and held in customer's account.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">
                          Reconciles customer's and the custodian's account records.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">
                          Visually inspects bars to confirm serial number, brand, and purity.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                        <span className="text-white font-bold text-sm">4</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">
                          Weighs a random sample of bars to the nearest thousandth of an ounce and record details of each bar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Download Modal */}
      <ReportDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default OutputsAndLiveDemo;
